import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { notesService } from '@/services/notesService'
import type { Database } from '@/integrations/supabase/types'
import type { NoteMapData } from '@/components/consultant/NoteMap'
import React from 'react'

type Note = Database['public']['Tables']['notes']['Row']
type NoteInsert = Database['public']['Tables']['notes']['Insert']

// Query Keys - Optimized hierarchical structure
export const notesQueryKeys = {
  all: ['notes'] as const,
  lists: () => [...notesQueryKeys.all, 'list'] as const,
  list: (userId: string) => [...notesQueryKeys.lists(), userId] as const,
  details: () => [...notesQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...notesQueryKeys.details(), id] as const,
  search: (userId: string, searchTerm: string) => [...notesQueryKeys.lists(), userId, 'search', searchTerm] as const,
  tags: (userId: string) => [...notesQueryKeys.all, 'tags', userId] as const,
}

// Custom hook for fetching notes
export function useNotesQuery(userId: string) {
  return useQuery({
    queryKey: notesQueryKeys.list(userId),
    queryFn: async () => {
      return await notesService.getNotes(userId)
    },
    staleTime: 1000 * 60 * 5, // 5 minutes - business data
    gcTime: 1000 * 60 * 30, // 30 minutes cache time
    refetchOnWindowFocus: false, // Notes don't need real-time updates
    enabled: !!userId,
  })
}

// Custom hook for searching notes
export function useNotesSearchQuery(userId: string, searchTerm: string) {
  return useQuery({
    queryKey: notesQueryKeys.search(userId, searchTerm),
    queryFn: async () => {
      if (!searchTerm.trim()) {
        return await notesService.getNotes(userId)
      }
      return await notesService.searchNotes(userId, searchTerm)
    },
    staleTime: 1000 * 60 * 2, // 2 minutes for search results
    gcTime: 1000 * 60 * 10, // 10 minutes cache time
    refetchOnWindowFocus: false, // Search results don't need real-time updates
    enabled: !!userId,
  })
}

// Custom hook for fetching user tags
export function useUserTagsQuery(userId: string) {
  return useQuery({
    queryKey: notesQueryKeys.tags(userId),
    queryFn: async () => {
      return await notesService.getUserTags(userId)
    },
    staleTime: 1000 * 60 * 10, // 10 minutes - static data
    gcTime: 1000 * 60 * 60, // 1 hour cache time
    refetchOnWindowFocus: false, // Tags don't change frequently
    enabled: !!userId,
  })
}

// Custom hook for creating notes
export function useCreateNoteMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (note: NoteInsert) => {
      return await notesService.createNote(note)
    },
    onMutate: async (newNote) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: notesQueryKeys.list(newNote.user_id) })

      // Snapshot the previous value
      const previousNotes = queryClient.getQueryData(notesQueryKeys.list(newNote.user_id))

      // Optimistically update to the new value
      queryClient.setQueryData(notesQueryKeys.list(newNote.user_id), (old: Note[] = []) => {
        const optimisticNote: Note = {
          ...newNote,
          id: `temp-${Date.now()}`, // Temporary ID for optimistic update
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        } as Note
        return [optimisticNote, ...old]
      })

      // Return a context object with the snapshotted value
      return { previousNotes }
    },
    onSuccess: (data, variables) => {
      // Replace the optimistic note with the real one
      queryClient.setQueryData(notesQueryKeys.list(variables.user_id), (old: Note[] = []) => {
        return old.map(note =>
          note.id.startsWith('temp-') ? data : note
        )
      })

      // Invalidate tags query
      queryClient.invalidateQueries({ queryKey: notesQueryKeys.tags(variables.user_id) })
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousNotes) {
        queryClient.setQueryData(notesQueryKeys.list(variables.user_id), context.previousNotes)
      }
      console.error('Failed to create note:', err)
    },
    onSettled: (data, error, variables) => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: notesQueryKeys.list(variables.user_id) })
    },
  })
}

// Custom hook for updating notes
export function useUpdateNoteMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ noteId, updates }: { noteId: string; updates: Partial<Note> }) => {
      return await notesService.updateNote(noteId, updates)
    },
    onMutate: async ({ noteId, updates }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: notesQueryKeys.all })

      // Snapshot the previous value
      const previousNotes = queryClient.getQueryData(notesQueryKeys.all)

      // Optimistically update to the new value
      queryClient.setQueryData(notesQueryKeys.all, (old: any) => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages?.map((page: any) =>
            page.map((note: Note) =>
              note.id === noteId ? { ...note, ...updates } : note
            )
          )
        }
      })

      // Return a context object with the snapshotted value
      return { previousNotes }
    },
    onSuccess: (data, variables) => {
      // Update was successful, data is already updated
      queryClient.invalidateQueries({ queryKey: notesQueryKeys.tags(data.user_id) })
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousNotes) {
        queryClient.setQueryData(notesQueryKeys.all, context.previousNotes)
      }
      console.error('Failed to update note:', err)
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: notesQueryKeys.all })
    },
  })
}

// Custom hook for debounced note updates (replaces useEffect auto-save)
export function useDebouncedNoteUpdate() {
  const queryClient = useQueryClient()
  const timeoutRef = React.useRef<NodeJS.Timeout | undefined>(undefined)

  const debouncedUpdate = React.useCallback((noteId: string, updates: Partial<Note>, delay: number = 1000) => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Set new timeout
    timeoutRef.current = setTimeout(async () => {
      try {
        await notesService.updateNote(noteId, updates)
        // Invalidate specific queries instead of all notes
        queryClient.invalidateQueries({ queryKey: notesQueryKeys.all })
      } catch (error) {
        console.error('Failed to auto-save note:', error)
      }
    }, delay)
  }, [queryClient])

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return debouncedUpdate
}

// Custom hook for deleting notes
export function useDeleteNoteMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (noteId: string) => {
      return await notesService.deleteNote(noteId)
    },
    onMutate: async (noteId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: notesQueryKeys.all })

      // Snapshot the previous value
      const previousNotes = queryClient.getQueryData(notesQueryKeys.all)

      // Optimistically remove the note
      queryClient.setQueryData(notesQueryKeys.all, (old: any) => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages?.map((page: any) =>
            page.filter((note: Note) => note.id !== noteId)
          )
        }
      })

      // Return a context object with the snapshotted value
      return { previousNotes }
    },
    onSuccess: () => {
      // Delete was successful, note is already removed
    },
    onError: (err, noteId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousNotes) {
        queryClient.setQueryData(notesQueryKeys.all, context.previousNotes)
      }
      console.error('Failed to delete note:', err)
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: notesQueryKeys.all })
    },
  })
}

// Query Keys for map operations
export const notesMapQueryKeys = {
  all: ['notes-maps'] as const,
  noteMap: (noteId: string) => [...notesMapQueryKeys.all, noteId] as const,
  notesWithMaps: (userId: string) => [...notesMapQueryKeys.all, 'with-maps', userId] as const,
}

// Custom hook for fetching note map data
export function useNoteMapQuery(noteId: string) {
  return useQuery({
    queryKey: notesMapQueryKeys.noteMap(noteId),
    queryFn: async () => {
      return await notesService.getNoteMapData(noteId)
    },
    staleTime: 1000 * 60 * 5, // 5 minutes - map data changes less frequently
    gcTime: 1000 * 60 * 30, // 30 minutes cache time
    refetchOnWindowFocus: false,
    enabled: !!noteId,
  })
}

// Custom hook for fetching notes with maps
export function useNotesWithMapsQuery(userId: string) {
  return useQuery({
    queryKey: notesMapQueryKeys.notesWithMaps(userId),
    queryFn: async () => {
      return await notesService.getNotesWithMaps(userId)
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    enabled: !!userId,
  })
}

// Custom hook for updating note map data
export function useUpdateNoteMapMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ noteId, mapData }: { noteId: string; mapData: NoteMapData }) => {
      return await notesService.updateNoteMapData(noteId, mapData)
    },
    onSuccess: (data, variables) => {
      // Update the note map query cache
      queryClient.setQueryData(notesMapQueryKeys.noteMap(variables.noteId), variables.mapData)
      // Also update the main notes query to reflect the updated note
      queryClient.invalidateQueries({ queryKey: notesQueryKeys.all })
      // Update notes with maps query
      queryClient.invalidateQueries({ queryKey: notesMapQueryKeys.notesWithMaps(data.user_id) })
    },
    onError: (error) => {
      console.error('Failed to update note map:', error)
    },
  })
}

// Custom hook for creating notes with maps
export function useCreateNoteWithMapMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ note, mapData }: { note: NoteInsert; mapData?: NoteMapData }) => {
      return await notesService.createNoteWithMap(note, mapData)
    },
    onSuccess: (data, variables) => {
      // Invalidate all note queries
      queryClient.invalidateQueries({ queryKey: notesQueryKeys.all })
      // Invalidate map queries
      queryClient.invalidateQueries({ queryKey: notesMapQueryKeys.all })
      // Invalidate tags query
      queryClient.invalidateQueries({ queryKey: notesQueryKeys.tags(data.user_id) })
    },
    onError: (error) => {
      console.error('Failed to create note with map:', error)
    },
  })
}
